import { useMemo } from 'react';
import { toBool } from 'vopay';
import { AccountAndBillingHostProps, BillingApi } from 'shared/accountAndBilling';
import { api } from '@/services/api.config';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { usePermissions } from '@/providers/Permissions.provider';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';

// The client's billing calls, built from its own api instance. This is client's side of the shared
// feature's host-varying-endpoint interface: the client hits the /account/* endpoints, the partner
// portal passes its own /partner/* equivalents. Static (only the module-singleton api), so it lives at
// module scope rather than being rebuilt per render.
const billingApi: BillingApi = {
  fetchInvoices: api.fetchAccountInvoices,
  fetchInvoicePdf: api.fetchAccountInvoicePdf,
  fetchStatement: async ({ InvoiceID }) => {
    const res = await api.fetchAccountStatementCsv({ InvoiceID });

    return { Success: res.Success, ErrorMessage: res.ErrorMessage, data: res.InvoiceCSV, format: 'csv' };
  },
  fetchDefaultPaymentMethod: api.fetchDefaultPaymentMethod,
  setDefaultPaymentMethod: api.postSetDefaultPaymentMethod,
};

// The one place client-dashboard maps its own user/account/permission model onto what the shared
// Account & Billing feature takes as props. Used where the feature is mounted (its route);
// partner-dashboard writes its own equivalent from its own providers.
export default function useBillingHostProps(): AccountAndBillingHostProps {
  const {
    response: { LoginUserName, Country, SubAccountType, ClientAccountsEnabled },
  } = useClientData();

  const {
    userSettings: { timezone },
  } = useUserSettingsProvider();

  const { CreditCardEnabled } = useLockedFeatures();

  const {
    permissions: { ManageAccountandBilling },
  } = usePermissions();

  return useMemo(
    () => ({
      viewer: { username: LoginUserName, timezone },
      account: {
        country: Country,
        isFranchise: SubAccountType === 'franchise',
        features: { creditCard: CreditCardEnabled ?? false, elinxDirectAdd: toBool(ClientAccountsEnabled) },
      },
      userPermissions: { canManageBilling: ManageAccountandBilling ?? false },
      billingApi,
    }),
    [
      LoginUserName,
      timezone,
      Country,
      SubAccountType,
      ClientAccountsEnabled,
      CreditCardEnabled,
      ManageAccountandBilling,
    ],
  );
}
